import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useTrackPageVisit } from '@/Domain/components/OnboardingProgress';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/Domain/components/Shared/Resizable/Resizable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { cn } from '@/Domain/utils/cn';
import { useIsMobileWidth } from '@/Domain/utils/isMobile';
import { AppearanceLoader } from '@/UI/Website/Appearance/Appearance.loader';
import { Install } from '@/UI/Website/Install/Install';
import { PreviewSidePanel, SIDE_PANEL_CONTAINER } from '@/UI/Website/PreviewSidePanel';
import { TextContentLoader } from '@/UI/Website/TextContent/TextContent.loader';
import { WidgetPreviewPanel } from '@/UI/Website/WidgetPreviewPanel';
import { usePreviewFocusToggle } from '@/UI/Website/usePreviewFocusToggle';

export interface ImplementationData {
  scenario_id: string;
  quickchatjs_url: string;
  api_server_url: string;
  chat_avatar: string;
  public_url_active: boolean;
  has_shopify_mcp_action: boolean;
}

enum TabsEnum {
  Install = 'install',
  Appearance = 'appearance',
  TextContent = 'text_content',
}

const VALID_TABS = new Set<string>(Object.values(TabsEnum));

export const WidgetSettingsPage = () => {
  const { t } = useTranslation('website');
  useTrackPageVisit('your_website');
  // Width-only (matches the TopBar Preview button's md:hidden): a wide tablet keeps the panels.
  const isMobile = useIsMobileWidth();
  const { leftPanelRef, isFocused, isLeftPanelCollapsed, toggleFocus, onLeftPanelCollapsedChange } =
    usePreviewFocusToggle();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(
    initialTab && VALID_TABS.has(initialTab) ? initialTab : TabsEnum.Install,
  );

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === TabsEnum.Install) {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', value);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const sideContent = (
    <div className="max-w-3xl mx-auto">
      <div className="flex gap-2 items-baseline mb-6">
        <h1 className="font-semibold text-lg">{t('title')}</h1>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value={TabsEnum.Install}>{t('tabs.install')}</TabsTrigger>
          <TabsTrigger value={TabsEnum.Appearance}>{t('tabs.appearance')}</TabsTrigger>
          <TabsTrigger value={TabsEnum.TextContent}>{t('tabs.text_content')}</TabsTrigger>
        </TabsList>

        <div className="mt-4">
          <TabsContent value={TabsEnum.Install} forceMount className="data-[state=inactive]:hidden">
            <Install />
          </TabsContent>
          <TabsContent
            value={TabsEnum.Appearance}
            forceMount
            className="data-[state=inactive]:hidden"
          >
            <AppearanceLoader />
          </TabsContent>
          <TabsContent
            value={TabsEnum.TextContent}
            forceMount
            className="data-[state=inactive]:hidden"
          >
            <TextContentLoader />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );

  return (
    <div className="-m-6 h-[calc(100svh-var(--header-height,3.25rem))] overflow-hidden">
      {isMobile ? (
        <div
          className={cn('h-full w-full', SIDE_PANEL_CONTAINER)}
          style={{ scrollbarGutter: 'stable' }}
        >
          {sideContent}
        </div>
      ) : (
        <ResizablePanelGroup direction="horizontal" autoSaveId="website-preview-panels">
          <PreviewSidePanel panelRef={leftPanelRef} onCollapsedChange={onLeftPanelCollapsedChange}>
            {sideContent}
          </PreviewSidePanel>

          <ResizableHandle withHandle className="bg-border" />

          <ResizablePanel id="preview-main" order={2} minSize={40}>
            <WidgetPreviewPanel
              isFocused={isFocused}
              isLeftPanelCollapsed={isLeftPanelCollapsed}
              onToggleFocus={toggleFocus}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </div>
  );
};
